/**
 * Scrap contract photos, browser side. Each contract keeps its own running
 * list of photos while it is open — not a shared day-based buffer like Andi —
 * and every picked file is squeezed under the size limit the same way, just
 * to a tighter target.
 */

export const SCRAP_MAX_BYTES = 450 * 1024;
const SCRAP_TARGET_BYTES = 380 * 1024;

const MAX_EDGE = 2400;
const MIN_EDGE = 640;
const START_QUALITY = 0.8;
const MIN_QUALITY = 0.35;
const MAX_ATTEMPTS = 7;

export type ScrapStatus = "open" | "done";

export type ScrapContract = {
  id: number;
  contractNumber: string;
  recordDate: string;
  status: ScrapStatus;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  photoCount: number;
  itemCount: number;
};

export type ScrapImage = {
  id: number;
  slot: number;
  fileName: string;
  contentType: string;
  byteSize: number;
  isCompressed: boolean;
};

export type ScrapItem = {
  id: number;
  contractId: number;
  itemSn: string;
  createdAt: string;
  images: ScrapImage[];
};

export type ScrapContractDetail = ScrapContract & { items: ScrapItem[] };

export function formatBytes(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Today's downloads read as a clock time; older ones get the date in front. */
export function formatTimestamp(isoTime: string, now = new Date()) {
  const stamp = new Date(isoTime);
  if (Number.isNaN(stamp.getTime())) return "";

  const time = stamp.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (stamp.toDateString() === now.toDateString()) return time;

  const day = String(stamp.getDate()).padStart(2, "0");
  const month = String(stamp.getMonth() + 1).padStart(2, "0");
  return `${month}.${day} ${time}`;
}

async function encodeJpeg(
  bitmap: ImageBitmap,
  edge: number,
  quality: number,
): Promise<Blob | null> {
  const scale = Math.min(1, edge / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));

  const context = canvas.getContext("2d");
  if (!context) return null;
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

/**
 * Squeezes one picked file under the 500 KB limit — quality first, then size,
 * the same two-step approach the Andi gallery uses, just aimed lower.
 */
/**
 * Squeezes one picked file under a size limit — quality first, then size.
 * Used directly by whichever tab needs a different limit than Scrap's own.
 */
export async function compressPhotoToLimit(
  file: File,
  maxBytes: number,
  targetBytes: number,
): Promise<{ file: File; error?: undefined } | { file?: undefined; error: string }> {
  if (!file.type.startsWith("image/")) {
    return { error: `${file.name}: only image files can be uploaded.` };
  }

  if (file.type === "image/jpeg" && file.size <= targetBytes) {
    return { file };
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return { error: `${file.name}: the image could not be read.` };
  }

  try {
    let edge = Math.min(MAX_EDGE, Math.max(bitmap.width, bitmap.height));
    let quality = START_QUALITY;
    let best: Blob | null = null;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
      const blob = await encodeJpeg(bitmap, edge, quality);
      if (!blob) break;

      best = blob;
      if (blob.size <= targetBytes) break;

      if (quality > 0.6) {
        quality = 0.6;
        continue;
      }

      const nextEdge = Math.max(
        MIN_EDGE,
        Math.round(edge * Math.min(0.9, Math.sqrt(targetBytes / blob.size))),
      );

      if (nextEdge >= edge) {
        if (quality <= MIN_QUALITY) break;
        quality = Math.max(MIN_QUALITY, quality - 0.1);
      } else {
        edge = nextEdge;
      }
    }

    if (!best) return { error: `${file.name}: compression failed.` };
    if (best.size > maxBytes) {
      return {
        error: `${file.name}: could not be compressed under ${Math.round(
          maxBytes / 1024,
        )} KB.`,
      };
    }

    const base = file.name.replace(/\.[^.]+$/, "") || "scrap";
    return { file: new File([best], `${base}.jpg`, { type: "image/jpeg" }) };
  } finally {
    bitmap.close();
  }
}

export function compressScrapPhoto(file: File) {
  return compressPhotoToLimit(file, SCRAP_MAX_BYTES, SCRAP_TARGET_BYTES);
}

// --- Mini preview (thumbnail) generation --------------------------------
//
// Every list view in the app that shows a picture (HW check photos, both
// Scrap tabs) shows a tiny copy generated here, never the full ~400 KB
// photo — the point of a thumbnail is not downloading the real thing just
// to shrink it with CSS afterward.

const THUMBNAIL_MAX_EDGE = 100;
const THUMBNAIL_QUALITY = 0.55;

/**
 * Builds a small JPEG (long edge ≤100px) from an already-compressed photo —
 * decoding it is free at that point, it is already sitting in memory. Returns
 * null rather than throwing, so a thumbnail failure never blocks the actual
 * upload; callers just skip attaching one.
 */
export async function makeThumbnail(file: File): Promise<File | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }

  try {
    const scale = Math.min(1, THUMBNAIL_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));

    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", THUMBNAIL_QUALITY),
    );
    if (!blob) return null;

    const base = file.name.replace(/\.[^.]+$/, "") || "photo";
    return new File([blob], `${base}-thumb.jpg`, { type: "image/jpeg" });
  } catch {
    return null;
  } finally {
    bitmap.close();
  }
}
